/** Default metadata for topology devices by type. */

let ipCounter = 1;

/** Default IP from counter (192.168.1.x). */
function generateDefaultIP() {
  const ip = `192.168.1.${ipCounter}`;
  ipCounter = (ipCounter % 254) + 1; // Wrap around at 254
  return ip;
}

/** Hostname from defaultLabel/type + nodeId. */
function generateDefaultHostname(nodeType, nodeId, defaultLabel) {
  const prefix = defaultLabel
    ? String(defaultLabel).trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || nodeType.replace(/_/g, '-')
    : nodeType.replace(/_/g, '-');
  const shortId = nodeId.split('-').pop() || nodeId;
  return `${prefix}-${shortId}`;
}

/** Default metadata for device type (static fallback when API has none). */
export function getDefaultMetadata(nodeType, nodeId, defaultLabel) {
  const hostname = generateDefaultHostname(nodeType, nodeId, defaultLabel);
  const ipAddress = generateDefaultIP();

  const defaults = {
    router: {
      ip_address: ipAddress,
      hostname: hostname,
      model: "Cisco ISR 4331",
      firmware_version: "16.09.04",
      management_interface: "GigabitEthernet0/0/0",
    },
    switch: {
      ip_address: ipAddress,
      hostname: hostname,
      vlan: "100",
      model: "Cisco Catalyst 2960",
      port_count: "24",
    },
    firewall: {
      ip_address: ipAddress,
      hostname: hostname,
      model: "Cisco ASA 5500",
      firmware_version: "9.8.2",
      security_level: "high",
    },
    server: {
      ip_address: ipAddress,
      hostname: hostname,
      os: "Ubuntu 22.04 LTS",
      ram_gb: "16",
      cpu_cores: "4",
    },
    vm: {
      ip_address: ipAddress,
      hostname: hostname,
      os: "Ubuntu 22.04 LTS",
      ram_gb: "8",
      cpu_cores: "2",
    },
    host_vm: {
      ip_address: ipAddress,
      hostname: hostname,
      os: "Ubuntu 22.04 LTS",
      ram_gb: "8",
      cpu_cores: "2",
    },
    database: {
      ip_address: ipAddress,
      hostname: hostname,
      database_type: "PostgreSQL",
      version: "14",
      port: "5432",
    },
    host: {
      ip_address: ipAddress,
      hostname: hostname,
      os: "Windows 11",
      mac_address: "00:00:00:00:00:00",
    },
    printer: {
      ip_address: ipAddress,
      hostname: hostname,
      model: "HP LaserJet Pro",
      location: "Office",
    },
    sensor: {
      ip_address: ipAddress,
      hostname: hostname,
      sensor_type: "IoT Sensor",
      protocol: "MQTT",
    },
    cdn: {
      ip_address: ipAddress,
      hostname: hostname,
      provider: "CloudFlare",
      region: "US-East",
    },
    proxy: {
      ip_address: ipAddress,
      hostname: hostname,
      port: "8080",
      protocol: "HTTP",
    },
    vpn: {
      ip_address: ipAddress,
      hostname: hostname,
      protocol: "OpenVPN",
      encryption: "AES-256",
    },
    custom_device: {
      ip_address: ipAddress,
      hostname: hostname,
      device_type: "Custom",
    },
  };

  return defaults[nodeType] || {
    ip_address: ipAddress,
    hostname: hostname,
    device_type: nodeType,
  };
}

/** Apply metadata: API template when non-empty, else static defaults; existing node.data wins. */
export function applyDefaultMetadata(node, nodeType, defaultLabel, apiMetadata = {}) {
  if (!node || !nodeType) return node;

  if (!node.data) node.data = {};

  const nodeId = node.id || nodeType;
  const shortId = nodeId.split("-").pop() || nodeId;

  const apiMeta =
    apiMetadata != null && typeof apiMetadata === "object"
      ? Object.fromEntries(
          Object.entries(apiMetadata).map(([k, v]) => {
            const s = v != null ? String(v) : "";
            const substituted = s
              .replace(/\{nodeId\}/gi, shortId)
              .replace(/\{node_id\}/gi, shortId);
            return [k, substituted];
          })
        )
      : {};

  const hasApiMetadata = Object.keys(apiMeta).length > 0;
  const base = hasApiMetadata
    ? apiMeta
    : getDefaultMetadata(nodeType, nodeId, defaultLabel ?? node.data?.label);

  const mergedData = {
    ...base,
    ...node.data,
  };

  node.data = mergedData;

  return node;
}

/** Reset IP counter. */
export function resetIPCounter() {
  ipCounter = 1;
}
